import {
  Box,
  HStack,
  Wrap,
  WrapItem,
  IconButton,
  Select,
  Tooltip,
  useColorModeValue,
  Divider,
  ButtonGroup,
  Text,
} from "@chakra-ui/react";
import { SmallCloseIcon, DownloadIcon } from "@chakra-ui/icons";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaAlignJustify,
  FaUndo,
  FaRedo,
  FaParagraph,
  FaHeading,
  FaQuoteLeft,
  FaListOl,
  FaListUl,
  FaImage,
  FaCode,
  FaTable,
  FaAngleUp,
  FaAngleDown,
  FaRegPlusSquare,
  FaRegMinusSquare,
  FaRemoveFormat,
  FaTrash,
  FaMinus,
  FaRegClone,
  FaWindowMinimize,
  FaRegFileCode,
  FaBug,
  FaObjectGroup,
  FaObjectUngroup,
  FaIndent,
  FaOutdent,
} from "react-icons/fa";
import HeadingInserter from "./HeadingInserter";

const ToolbarButton = ({ label, icon, onClick, isActive = false, isDisabled = false }) => (
  <Tooltip label={label} hasArrow openDelay={300}>
    <IconButton
      aria-label={label}
      icon={icon}
      onClick={onClick}
      size="sm"
      variant={isActive ? "solid" : "ghost"}
      colorScheme={isActive ? "linkedin" : undefined}
      borderRadius="lg"
      isDisabled={isDisabled}
    />
  </Tooltip>
);

const TextButton = ({ label, onClick, isActive = false, children }) => (
  <Tooltip label={label} hasArrow openDelay={300}>
    <IconButton
      aria-label={label}
      onClick={onClick}
      size="sm"
      variant={isActive ? "solid" : "ghost"}
      colorScheme={isActive ? "linkedin" : undefined}
      borderRadius="lg"
      icon={
        <Text fontWeight="semibold" fontSize="sm" lineHeight="1">
          {children}
        </Text>
      }
    />
  </Tooltip>
);

export default function MenuBar({ editor, onSave, onClose, onShowCodified, onCodifyCurrentRow, debugMode, onToggleDebug, isSuperAdmin, codifyCollection, documentDefinitionId }) {
  const bg = useColorModeValue("white", "gray.800");
  if (!editor) return null;

  const changeFontSize = (delta) => {
    const sel = window.getSelection();
    if (!sel?.anchorNode?.parentElement) return;
    const style = window.getComputedStyle(sel.anchorNode.parentElement);
    const current = parseFloat(style.fontSize || "16");
    const next = `${Math.max(8, current + delta)}px`;
    editor.chain().focus().setFontSize(next).run();
  };

  const handleMergeCells = () => {
    const { state } = editor;
    const { selection } = state;

    // Before merging, capture the row's attributes (especially headingType and id)
    let rowAttrs = {};
    for (let d = selection.$from.depth; d > 0; d--) {
      const node = selection.$from.node(d);
      if (node.type.name === "tableRow") {
        rowAttrs = { ...node.attrs };
        break;
      }
    }

    console.log('[MERGE] Row attrs before merge:', rowAttrs);

    // Merge the cells first
    editor.chain().focus().mergeCells().run();

    // After merging, set the merged cell's role to "content" and preserve row attributes
    setTimeout(() => {
      const { state } = editor;
      const { selection } = state;

      // Find the cell we're in after merging
      for (let d = selection.$from.depth; d > 0; d--) {
        const node = selection.$from.node(d);
        if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
          const cellPos = selection.$from.before(d);

          const newAttrs = {
            ...node.attrs,
            role: "content",
            headingType: rowAttrs.headingType || node.attrs.headingType || "Unknown"
          };

          console.log('[MERGE] Setting merged cell attrs:', newAttrs);

          // Set cell role to "content" and inherit headingType from row
          const tr = state.tr.setNodeMarkup(cellPos, undefined, newAttrs);
          editor.view.dispatch(tr);
          break;
        }
      }
    }, 0);
  };

  const changeLineHeight = (delta) => {
    const sel = window.getSelection();
    if (!sel?.anchorNode?.parentElement) return;
    const style = window.getComputedStyle(sel.anchorNode.parentElement);
    const current = parseFloat(style.lineHeight || "1.5");
    const next = Math.max(1.0, current + delta).toFixed(1);
    editor.chain().focus().setMark("textStyle", { lineHeight: next }).run();
  };

  // Active state helpers (if your extensions support them)
  const isActive = (fn) => {
    try {
      return fn();
    } catch {
      return false;
    }
  };

  // Read current font size (falls back to empty if none)
  const currentSize = editor.getAttributes("textStyle")?.fontSize ?? "";

  const setFontSizePx = (n) => {
    if (!n) return;
    const px = `${parseInt(n, 10)}px`;

    const { state } = editor;
    const { selection, tr } = state;

    // Apply font size to the entire selection, replacing any existing font size
    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (node.isText) {
        const from = Math.max(pos, selection.from);
        const to = Math.min(pos + node.nodeSize, selection.to);

        // Get current marks and update fontSize
        const marks = node.marks.filter(m => m.type.name !== 'textStyle' || !m.attrs.fontSize);
        const fontSizeMark = state.schema.marks.textStyle.create({ fontSize: px });

        tr.addMark(from, to, fontSizeMark);
      }
    });

    editor.view.dispatch(tr);
    editor.commands.focus();
  };

  // Common sizes for dropdown; tweak as you like
  const FONT_SIZES = [
    8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48, 64,
  ];

  return (
    <Box
      position="sticky"
      top="0"
      zIndex="100"
      bg={bg}
      sx={{ backdropFilter: "saturate(1.2) blur(6px)" }}>
      <HStack align="left">
        {/* Left side: grouped controls */}
        <Wrap align="top" shouldWrapChildren>
          {/* Undo / Redo */}
          <WrapItem display="flex" flexDirection="column">
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Undo
              </Text>
              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Undo"
                  icon={<FaUndo />}
                  onClick={() => editor.chain().focus().undo().run()}
                />
                <ToolbarButton
                  label="Redo"
                  icon={<FaRedo />}
                  onClick={() => editor.chain().focus().redo().run()}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Block type */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Block
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Paragraph"
                  icon={<FaParagraph />}
                  onClick={() => editor.chain().focus().setParagraph().run()}
                  isActive={isActive(() => editor.isActive("paragraph"))}
                />
                <ToolbarButton
                  label="H1"
                  icon={<FaHeading />}
                  text="1"
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  }
                  isActive={isActive(() =>
                    editor.isActive("heading", { level: 1 })
                  )}
                />
                <ToolbarButton
                  label="H2"
                  icon={<FaHeading />}
                  text="2"
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  }
                  isActive={isActive(() =>
                    editor.isActive("heading", { level: 2 })
                  )}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Block type */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Pagination
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Insert Page Break"
                  icon={<FaRegClone />}
                  onClick={() => editor?.commands.insertPageBreak()}
                  isActive={isActive(() => editor.isActive("pageBreak"))}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Inline styles */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Font Style
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Bold"
                  icon={<FaBold />}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  isActive={isActive(() => editor.isActive("bold"))}
                />
                <ToolbarButton
                  label="Italic"
                  icon={<FaItalic />}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  isActive={isActive(() => editor.isActive("italic"))}
                />
                <ToolbarButton
                  label="Underline"
                  icon={<FaUnderline />}
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  isActive={isActive(() => editor.isActive("underline"))}
                />
                <ToolbarButton
                  label="Strike"
                  icon={<FaStrikethrough />}
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  isActive={isActive(() => editor.isActive("strike"))}
                />
                <ToolbarButton
                  label="Code"
                  icon={<FaCode />}
                  onClick={() => editor.chain().focus().toggleCode().run()}
                  isActive={isActive(() => editor.isActive("code"))}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Font Size */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Font Size
              </Text>

              <HStack spacing={2}>
                {/* Exact number select (compact) */}
                <Select
                  size="sm"
                  width="90px"
                  value={currentSize?.replace("px", "") || ""}
                  placeholder="px"
                  onChange={(e) => setFontSizePx(e.target.value)}>
                  {FONT_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </HStack>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Alignment */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Alignment
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Align Left"
                  icon={<FaAlignLeft />}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("left").run()
                  }
                  isActive={isActive(() =>
                    editor.isActive({ textAlign: "left" })
                  )}
                />
                <ToolbarButton
                  label="Center"
                  icon={<FaAlignCenter />}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("center").run()
                  }
                  isActive={isActive(() =>
                    editor.isActive({ textAlign: "center" })
                  )}
                />
                <ToolbarButton
                  label="Right"
                  icon={<FaAlignRight />}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("right").run()
                  }
                  isActive={isActive(() =>
                    editor.isActive({ textAlign: "right" })
                  )}
                />
                <ToolbarButton
                  label="Justify"
                  icon={<FaAlignJustify />}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("justify").run()
                  }
                  isActive={isActive(() =>
                    editor.isActive({ textAlign: "justify" })
                  )}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Lists / Blocks */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Lists
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Bulleted List"
                  icon={<FaListUl />}
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  isActive={isActive(() => editor.isActive("bulletList"))}
                />
                <ToolbarButton
                  label="Numbered List"
                  icon={<FaListOl />}
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  isActive={isActive(() => editor.isActive("orderedList"))}
                />
                <ToolbarButton
                  label="Blockquote"
                  icon={<FaQuoteLeft />}
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                  isActive={isActive(() => editor.isActive("blockquote"))}
                />
                <ToolbarButton
                  label="Horizontal Rule"
                  icon={<FaWindowMinimize />}
                  onClick={() =>
                    editor.chain().focus().setHorizontalRule().run()
                  }
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Indent Controls */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Indent
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Decrease Indent"
                  icon={<FaOutdent />}
                  onClick={() => {
                    // Check if we're in a list
                    if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
                      editor.chain().focus().liftListItem('listItem').run();
                    } else {
                      // For regular paragraphs, decrease text indent
                      const currentAttrs = editor.getAttributes('paragraph');
                      const currentIndent = currentAttrs.textIndent || '0em';
                      const currentValue = parseFloat(currentIndent) || 0;
                      const newIndent = Math.max(0, currentValue - 2);

                      editor.chain().focus().updateAttributes('paragraph', {
                        textIndent: newIndent > 0 ? `${newIndent}em` : null
                      }).run();
                    }
                  }}
                />
                <ToolbarButton
                  label="Increase Indent"
                  icon={<FaIndent />}
                  onClick={() => {
                    // Check if we're in a list
                    if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
                      editor.chain().focus().sinkListItem('listItem').run();
                    } else {
                      // For regular paragraphs, increase text indent
                      const currentAttrs = editor.getAttributes('paragraph');
                      const currentIndent = currentAttrs.textIndent || '0em';
                      const currentValue = parseFloat(currentIndent) || 0;
                      const newIndent = currentValue + 2;

                      editor.chain().focus().updateAttributes('paragraph', {
                        textIndent: `${newIndent}em`
                      }).run();
                    }
                  }}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Image
              </Text>
              <ButtonGroup>
                <ToolbarButton
                  label="Add Image"
                  icon={<FaImage />}
                  onClick={() => editor.chain().focus().addImage().run()}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>
          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Table */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Table
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Insert Table"
                  icon={<FaTable />}
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  }
                />
                <ToolbarButton
                  label="Delete Table"
                  icon={<FaTrash />}
                  onClick={() => editor.chain().focus().deleteTable().run()}
                />
                <ToolbarButton
                  label="Clear Formatting"
                  icon={<FaRemoveFormat />}
                  onClick={() => 
                    editor.chain()
                      .focus()
                      .clearNodes()
                      .unsetAllMarks()
                      .run()
                  }
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Table
              </Text>

              <HStack spacing={1}>
                <ButtonGroup isAttached variant="ghost" size="sm">
                  <ToolbarButton
                    label="Add Row After"
                    icon={<FaRegPlusSquare />}
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                    isDisabled={!editor.isActive('table')}
                  />
                  <ToolbarButton
                    label="Delete Row"
                    icon={<FaRegMinusSquare />}
                    onClick={() => editor.chain().focus().deleteRow().run()}
                    isDisabled={!editor.isActive('table')}
                  />
                </ButtonGroup>
                <ButtonGroup isAttached variant="ghost" size="sm">
                  <ToolbarButton
                    label="Add Column After"
                    icon={<FaRegPlusSquare />}
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                    isDisabled={!editor.isActive('table')}
                  />
                  <ToolbarButton
                    label="Delete Column"
                    icon={<FaRegMinusSquare />}
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                    isDisabled={!editor.isActive('table')}
                  />
                </ButtonGroup>
                <ButtonGroup isAttached variant="ghost" size="sm">
                  <ToolbarButton
                    label="Merge Cells"
                    icon={<FaObjectGroup />}
                    onClick={handleMergeCells}
                    isDisabled={!editor.isActive('table')}
                  />
                  <ToolbarButton
                    label="Split Cell"
                    icon={<FaObjectUngroup />}
                    onClick={() => editor.chain().focus().splitCell().run()}
                    isDisabled={!editor.isActive('table')}
                  />
                </ButtonGroup>
              </HStack>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          {/* Line Height tweaks */}
          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Line Height
              </Text>

              <ButtonGroup isAttached variant="ghost" size="sm">
                <ToolbarButton
                  label="Line Height -"
                  icon={<FaAngleDown />}
                  onClick={() => changeLineHeight(-0.1)}
                />
                <ToolbarButton
                  label="Line Height +"
                  icon={<FaAngleUp />}
                  onClick={() => changeLineHeight(0.1)}
                />
              </ButtonGroup>
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1}>
                Headings
              </Text>
              {/* Right side: inserter + actions */}

              <HeadingInserter editor={editor} codifyCollection={codifyCollection} documentDefinitionId={documentDefinitionId} />
            </Box>
          </WrapItem>

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />

          <WrapItem>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="medium" mb={1} textAlign="center">
                Codify
              </Text>
              <HStack>
                <Tooltip label="Codify Current Row (Ctrl+R)">
                  <IconButton
                    icon={<FaRedo />}
                    aria-label="Codify Current Row"
                    onClick={onCodifyCurrentRow}
                    size="sm"
                  />
                </Tooltip>
                <Tooltip label="Show Codified Contents">
                  <IconButton
                    icon={<FaRegFileCode />}
                    aria-label="Show Codified Contents"
                    onClick={onShowCodified}
                    size="sm"
                  />
                </Tooltip>
              </HStack>
            </Box>
          </WrapItem>

          {isSuperAdmin && (
            <>
              <Divider
                orientation="vertical"
                borderColor="gray.300"
                borderWidth="1px"
                alignSelf="stretch"
              />

              <WrapItem>
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" mb={1} textAlign="center">
                    Debug
                  </Text>
                  <HStack>
                    <Tooltip label={debugMode ? "Disable Debug Mode" : "Enable Debug Mode"}>
                      <IconButton
                        icon={<FaBug />}
                        aria-label="Toggle Debug Mode"
                        onClick={onToggleDebug}
                        variant={debugMode ? "solid" : "ghost"}
                        colorScheme={debugMode ? "red" : undefined}
                        size="sm"
                      />
                    </Tooltip>
                  </HStack>
                </Box>
              </WrapItem>
            </>
          )}

          <Divider
            orientation="vertical"
            borderColor="gray.300"
            borderWidth="1px"
            alignSelf="stretch"
          />
        </Wrap>

        {/*<Spacer /> */}
      </HStack>
    </Box>
  );
}
